// Polar mapping between the server's flat (cx, cy) grid and the visual
// disk-slice layout for boss mode. cx → radius (distance from hub), cy →
// angle (angular position within the slice).
//
// Cells are integer cy values from yMin to yMax inclusive. A click at world
// position with dAngle == 0 should map to the CENTRE of the lane-row cell,
// which in continuous flat coords sits at cy = laneRow + 0.5. Likewise the
// band spans (yMax + 1) - yMin continuous units, so bandHalfContinuous is
// that divided by two.
export class LaneTransform {
  private readonly bandCentre: number;
  private readonly bandHalfCont: number;
  private readonly cxCentre: number;
  private readonly cxHalfCont: number;

  constructor(
    public readonly sliceCenterAngle: number, // rad
    public readonly sliceAngularSpan: number, // rad
    public readonly hubRadius: number,
    public readonly outerRadius: number,
    public readonly laneRow: number, // integer cy of the lane row (path centre)
    public readonly bandHalf: number, // (yMax - yMin) / 2, integer cells
    public readonly minCx: number, // integer cx of spawn cell
    public readonly maxCx: number, // integer cx of base cell
  ) {
    // Continuous mapping: cells span cy in [yMin, yMax + 1) so the centre
    // sits at (yMin + yMax + 1) / 2. With yMin = laneRow - bandHalf and
    // yMax = laneRow + bandHalf, that simplifies to laneRow + 0.5.
    this.bandCentre = laneRow + 0.5;
    this.bandHalfCont = bandHalf + 0.5;
    // Same idea on the radial axis: cells span cx in [minCx, maxCx + 1).
    this.cxCentre = (minCx + maxCx + 1) / 2;
    this.cxHalfCont = (maxCx + 1 - minCx) / 2;
  }

  // Local lane coordinates (continuous cx, cy) → world XZ.
  flatToWorld(flatX: number, flatY: number): { x: number; z: number } {
    const tx = (flatX - this.cxCentre) / Math.max(0.0001, this.cxHalfCont);
    const radius = this.midRadius() + tx * this.radialHalf();
    const ty = (flatY - this.bandCentre) / Math.max(0.0001, this.bandHalfCont);
    const angle = this.sliceCenterAngle + ty * (this.sliceAngularSpan / 2);
    return {
      x: Math.cos(angle) * radius,
      z: Math.sin(angle) * radius,
    };
  }

  // World XZ → flat integer cell. Returns null if outside the slice annulus.
  worldToFlat(wx: number, wz: number): { cx: number; cy: number } | null {
    const radius = Math.sqrt(wx * wx + wz * wz);
    if (radius < this.hubRadius - 1 || radius > this.outerRadius + 1) return null;
    const angle = Math.atan2(wz, wx);
    let dAngle = angle - this.sliceCenterAngle;
    while (dAngle > Math.PI) dAngle -= Math.PI * 2;
    while (dAngle < -Math.PI) dAngle += Math.PI * 2;
    if (Math.abs(dAngle) > this.sliceAngularSpan / 2 + 0.05) return null;
    const tx = (radius - this.midRadius()) / Math.max(0.0001, this.radialHalf());
    const ty = dAngle / Math.max(0.0001, this.sliceAngularSpan / 2);
    const cxCont = this.cxCentre + tx * this.cxHalfCont;
    const cyCont = this.bandCentre + ty * this.bandHalfCont;
    return { cx: Math.floor(cxCont), cy: Math.floor(cyCont) };
  }

  worldAngleAt(flatY: number): number {
    const ty = (flatY - this.bandCentre) / Math.max(0.0001, this.bandHalfCont);
    return this.sliceCenterAngle + ty * (this.sliceAngularSpan / 2);
  }

  radiusAt(flatX: number): number {
    const tx = (flatX - this.cxCentre) / Math.max(0.0001, this.cxHalfCont);
    return this.midRadius() + tx * this.radialHalf();
  }

  private midRadius(): number {
    return (this.hubRadius + this.outerRadius) / 2;
  }

  private radialHalf(): number {
    return (this.outerRadius - this.hubRadius) / 2;
  }
}
